(() => {
  const selectors = {
    root: '[data-issi-gift-finder]',
    open: '[data-issi-gift-finder-open]',
    panel: '[data-issi-gift-finder-panel]',
    dialog: '[data-issi-gift-finder-dialog]',
    close: '[data-issi-gift-finder-close]',
    backdrop: '[data-issi-gift-finder-backdrop]',
    form: '[data-issi-gift-finder-form]',
    step: '[data-issi-gift-finder-step]',
    indicator: '[data-issi-gift-finder-indicator]',
    counter: '[data-issi-gift-finder-counter]',
    previous: '[data-issi-gift-finder-prev]',
    next: '[data-issi-gift-finder-next]',
    restart: '[data-issi-gift-finder-restart]',
    error: '[data-issi-gift-finder-error]',
    results: '[data-issi-gift-finder-results]',
    resultsMessage: '[data-issi-gift-finder-results-message]',
    resultGrid: '[data-issi-gift-finder-result-grid]',
    noResults: '[data-issi-gift-finder-no-results]',
    productData: '[data-issi-gift-finder-products]',
  };

  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');

  const defaultConfig = {
    maxResults: 3,
    fallbackMessage: '',
    labels: {
      viewProduct: '',
      recommendationReason: '__reason__',
      fallbackBadge: '',
    },
    sourceProducts: [],
    fallbackProducts: [],
  };

  class IssiGiftFinder {
    constructor(root) {
      this.root = root;
      this.mode = root.dataset.issiMode === 'inline' ? 'inline' : 'popup';
      this.isPopup = this.mode === 'popup';
      this.stepTemplate = root.dataset.issiStepTemplate || '';
      this.openButton = root.querySelector(selectors.open);
      this.panel = root.querySelector(selectors.panel);
      this.dialog = root.querySelector(selectors.dialog);
      this.closeButton = root.querySelector(selectors.close);
      this.backdrop = root.querySelector(selectors.backdrop);
      this.form = root.querySelector(selectors.form);
      this.steps = Array.from(root.querySelectorAll(selectors.step));
      this.indicators = Array.from(root.querySelectorAll(selectors.indicator));
      this.counter = root.querySelector(selectors.counter);
      this.previousButton = root.querySelector(selectors.previous);
      this.nextButton = root.querySelector(selectors.next);
      this.restartButton = root.querySelector(selectors.restart);
      this.error = root.querySelector(selectors.error);
      this.results = root.querySelector(selectors.results);
      this.resultsMessage = root.querySelector(selectors.resultsMessage);
      this.resultGrid = root.querySelector(selectors.resultGrid);
      this.noResults = root.querySelector(selectors.noResults);
      this.config = this.parseProductData();
      this.maxResults = Math.max(1, Number(this.config.maxResults) || defaultConfig.maxResults);
      this.defaultResultsMessage = this.resultsMessage?.textContent.trim() || '';
      this.activeStep = 1;
      this.lastFocusedElement = null;
      this.overlayCloseEnabled = root.dataset.issiOverlayClose === 'true';
      this.onKeydown = this.onKeydown.bind(this);

      this.bindEvents();
      this.showStep(1, { focus: false });
    }

    bindEvents() {
      this.openButton?.addEventListener('click', () => this.open());
      this.closeButton?.addEventListener('click', () => this.close());
      this.previousButton?.addEventListener('click', () => this.previous());
      this.nextButton?.addEventListener('click', () => this.next());
      this.restartButton?.addEventListener('click', () => this.restart());
      this.form?.addEventListener('submit', (event) => event.preventDefault());
      this.form?.addEventListener('change', (event) => {
        this.clearError();
        if (event.target?.name === 'recipient') this.updateAudienceOptions({ reset: true });
      });

      this.backdrop?.addEventListener('click', () => {
        if (this.overlayCloseEnabled) this.close();
      });
    }

    parseProductData() {
      const dataElement = this.root.querySelector(selectors.productData);
      if (!dataElement) return { ...defaultConfig };

      try {
        const parsed = JSON.parse(dataElement.textContent || '{}');
        const isVisibleProduct = (product) => {
          return !product?.isAddon && !(Array.isArray(product?.tags) && product.tags.includes('hidden-addon'));
        };

        return {
          ...defaultConfig,
          ...parsed,
          labels: {
            ...defaultConfig.labels,
            ...(parsed.labels || {}),
          },
          sourceProducts: Array.isArray(parsed.sourceProducts) ? parsed.sourceProducts.filter(isVisibleProduct) : [],
          fallbackProducts: Array.isArray(parsed.fallbackProducts) ? parsed.fallbackProducts.filter(isVisibleProduct) : [],
        };
      } catch (error) {
        return { ...defaultConfig };
      }
    }

    open() {
      if (!this.panel || !this.dialog) return;

      this.lastFocusedElement = document.activeElement;
      this.panel.hidden = false;
      this.openButton?.setAttribute('aria-expanded', 'true');
      this.root.classList.add('is-open');
      document.addEventListener('keydown', this.onKeydown);

      requestAnimationFrame(() => this.focusFirstElement());
    }

    close() {
      if (!this.panel) return;

      this.panel.hidden = true;
      this.openButton?.setAttribute('aria-expanded', 'false');
      this.root.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      this.clearError();

      if (this.lastFocusedElement && typeof this.lastFocusedElement.focus === 'function') {
        this.lastFocusedElement.focus();
      }
    }

    next() {
      if (!this.results?.hidden) return;
      if (!this.currentStepIsValid()) return;

      if (this.activeStep < this.steps.length) {
        this.showStep(this.activeStep + 1);
        return;
      }

      this.showResults();
    }

    previous() {
      if (!this.results?.hidden) {
        this.results.hidden = true;
        this.restartButton.hidden = true;
        this.showStep(this.steps.length);
        return;
      }

      if (this.activeStep > 1) this.showStep(this.activeStep - 1);
    }

    restart() {
      this.form?.reset();
      this.updateAudienceOptions({ reset: true });
      this.clearResults();
      this.results.hidden = true;
      this.restartButton.hidden = true;
      this.showStep(1);
    }

    currentStepIsValid() {
      const step = this.steps[this.activeStep - 1];
      if (!step) return true;

      const checked = step.querySelector('input[type="radio"]:checked:not(:disabled)');
      if (checked) {
        this.clearError();
        return true;
      }

      this.steps.forEach((item) => item.classList.remove('has-error'));
      step.classList.add('has-error');

      if (this.error) this.error.hidden = false;

      const firstInput = step.querySelector('input[type="radio"]:not(:disabled)');
      if (firstInput) firstInput.focus();

      return false;
    }

    showStep(stepNumber, options = { focus: true }) {
      this.activeStep = stepNumber;
      this.clearError();
      this.updateAudienceOptions();

      this.steps.forEach((step) => {
        step.hidden = Number(step.dataset.issiGiftFinderStep) !== stepNumber;
      });

      this.indicators.forEach((indicator) => {
        indicator.classList.toggle('is-active', Number(indicator.dataset.issiGiftFinderIndicator) <= stepNumber);
      });

      if (this.counter && this.stepTemplate) {
        this.counter.textContent = this.stepTemplate.replace('__current__', stepNumber);
      }

      if (this.results) this.results.hidden = true;
      if (this.previousButton) this.previousButton.hidden = stepNumber === 1;
      if (this.nextButton) {
        this.nextButton.hidden = false;
        this.nextButton.textContent = stepNumber === this.steps.length
          ? this.nextButton.dataset.issiResultsLabel
          : this.nextButton.dataset.issiNextLabel;
      }
      if (this.restartButton) this.restartButton.hidden = true;

      if (options.focus) {
        requestAnimationFrame(() => {
          const step = this.steps[stepNumber - 1];
          const selected = step?.querySelector('input[type="radio"]:checked:not(:disabled)');
          const first = selected || step?.querySelector('input[type="radio"]:not(:disabled)') || step;
          first?.focus();
        });
      }
    }

    updateAudienceOptions(options = { reset: false }) {
      const audience = this.form?.querySelector('input[name="recipient"]:checked')?.value || '';
      const relationshipOptions = Array.from(this.form?.querySelectorAll('input[name="occasion"]') || []);

      relationshipOptions.forEach((input) => {
        const optionAudience = input.dataset.issiAudience || 'all';
        const isVisible = !audience || optionAudience === 'all' || optionAudience === audience;
        const option = input.closest('.issi-gift-finder__option');

        input.disabled = !isVisible;
        if (option) option.hidden = !isVisible;
        if (!isVisible || options.reset) input.checked = false;
      });
    }

    showResults() {
      const recommendations = this.getRecommendations();

      this.clearError();
      this.steps.forEach((step) => {
        step.hidden = true;
      });

      this.renderResults(recommendations);
      this.results.hidden = false;
      this.previousButton.hidden = false;
      this.nextButton.hidden = true;
      this.restartButton.hidden = false;
      this.results.focus();
    }

    getSelections() {
      return ['recipient', 'occasion', 'style', 'budget'].map((name) => {
        const input = this.form?.querySelector(`input[name="${name}"]:checked:not(:disabled)`);
        if (!input) return null;

        const label = input.closest('.issi-gift-finder__option')?.textContent.trim() || input.value;
        return {
          name,
          label,
          value: input.value,
          tagMatches: this.splitMatchValues(input.dataset.issiTagMatch || ''),
          collectionMatches: this.splitMatchValues(input.dataset.issiCollectionMatch || ''),
          titleMatches: this.splitMatchValues(input.dataset.issiTitleMatch || ''),
          maxPrice: this.parseOptionalNumber(input.dataset.issiMaxPrice),
          weight: Math.max(1, Number(input.dataset.issiWeight) || 1),
        };
      }).filter(Boolean);
    }

    parseOptionalNumber(value) {
      const number = Number(value);
      return Number.isFinite(number) ? number : null;
    }

    splitMatchValues(value) {
      return String(value || '')
        .split(/[|,\n]/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    getRecommendations() {
      const selections = this.getSelections();
      const sourceItems = this.scoreProducts(this.config.sourceProducts, selections)
        .filter((item) => item.score > 0)
        .slice(0, this.maxResults);

      if (sourceItems.length > 0) {
        return { items: sourceItems, isFallback: false, hasResults: true };
      }

      const fallbackPool = this.config.fallbackProducts.length > 0
        ? this.config.fallbackProducts
        : this.config.sourceProducts;

      const fallbackItems = this.scoreProducts(fallbackPool, selections)
        .slice(0, this.maxResults)
        .map((item) => ({
          ...item,
          reasons: item.reasons.length ? item.reasons : [],
        }));

      return {
        items: fallbackItems,
        isFallback: fallbackItems.length > 0,
        hasResults: fallbackItems.length > 0,
      };
    }

    scoreProducts(products, selections) {
      return this.availableProducts(products)
        .map((product) => this.scoreProduct(product, selections))
        .sort((a, b) => {
          if (b.score !== a.score) return b.score - a.score;
          return Number(a.product.price || 0) - Number(b.product.price || 0);
        });
    }

    availableProducts(products) {
      return (Array.isArray(products) ? products : []).filter((product) => product?.available === true);
    }

    scoreProduct(product, selections) {
      const tags = Array.isArray(product.tags) ? product.tags.map((tag) => this.normalize(tag)) : [];
      const collections = Array.isArray(product.collections)
        ? product.collections.map((collection) => this.normalize(collection))
        : [];
      const title = this.normalize(product.title);
      const productType = this.normalize(product.productType);
      const vendor = this.normalize(product.vendor);
      const reasons = [];
      let score = 0;

      selections.forEach((selection) => {
        const selectionScore = this.scoreSelection(selection, {
          tags,
          collections,
          title,
          productType,
          vendor,
          price: Number(product.price || 0),
        });

        if (selectionScore > 0) {
          score += selectionScore;
          if (!reasons.includes(selection.label)) reasons.push(selection.label);
        }
      });

      return { product, score, reasons };
    }

    scoreSelection(selection, product) {
      if (selection.name === 'budget') {
        return this.matchesBudget(product.price, selection) ? selection.weight * 10 : 0;
      }

      const normalizedValue = this.normalize(selection.value);
      const tagMatches = selection.tagMatches.map((value) => this.normalize(value)).filter(Boolean);
      const collectionMatches = selection.collectionMatches.map((value) => this.normalize(value)).filter(Boolean);
      const titleMatches = selection.titleMatches.map((value) => this.normalize(value)).filter(Boolean);
      const candidates = Array.from(new Set([normalizedValue, ...tagMatches].filter(Boolean)));
      let score = 0;

      candidates.forEach((candidate) => {
        if (product.tags.includes(candidate)) score = Math.max(score, selection.weight * 12);
        if (product.tags.some((tag) => tag.includes(candidate))) score = Math.max(score, selection.weight * 7);
        if (product.productType === candidate || product.vendor === candidate) score = Math.max(score, selection.weight * 5);
      });

      collectionMatches.forEach((candidate) => {
        if (product.collections.includes(candidate)) score = Math.max(score, selection.weight * 9);
      });

      titleMatches.forEach((candidate) => {
        if (product.title.includes(candidate)) score = Math.max(score, selection.weight * 4);
      });

      return score;
    }

    matchesBudget(price, selection) {
      if (!Number.isFinite(price) || price <= 0) return false;

      const maxPrice = Number(selection.maxPrice || 0);

      if (maxPrice > 0) return price <= maxPrice;

      const budgetValue = selection.value;
      if (budgetValue === 'budget_any') return true;
      if (budgetValue === 'budget_25') return price <= 2500;
      if (budgetValue === 'budget_50') return price > 2500 && price <= 5000;
      if (budgetValue === 'budget_100') return price > 5000 && price <= 10000;
      if (budgetValue === 'budget_over_100') return price > 10000;

      return false;
    }

    normalize(value) {
      return String(value || '')
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase()
        .trim();
    }

    renderResults(resultSet) {
      this.clearResults();

      if (this.resultsMessage) {
        this.resultsMessage.textContent = resultSet.isFallback && this.config.fallbackMessage
          ? this.config.fallbackMessage
          : this.defaultResultsMessage;
      }

      if (!resultSet.hasResults) {
        if (this.noResults) this.noResults.hidden = false;
        return;
      }

      resultSet.items.forEach((item) => {
        this.resultGrid?.appendChild(this.createProductCard(item, resultSet.isFallback));
      });
    }

    clearResults() {
      this.resultGrid?.replaceChildren();
      if (this.noResults) this.noResults.hidden = true;
      if (this.resultsMessage) this.resultsMessage.textContent = this.defaultResultsMessage;
    }

    clearError() {
      this.steps.forEach((step) => step.classList.remove('has-error'));
      if (this.error) this.error.hidden = true;
    }

    createProductCard(item, isFallback) {
      const product = item.product;
      const article = document.createElement('article');
      article.className = 'product-card-wrapper issi-gift-finder__result-card';

      const media = document.createElement('a');
      media.className = 'issi-gift-finder__result-media';
      media.href = product.url || '#';

      if (product.featuredImage) {
        const image = document.createElement('img');
        image.src = product.featuredImage;
        image.alt = product.title || '';
        image.loading = 'lazy';
        media.appendChild(image);
      }

      const body = document.createElement('div');
      body.className = 'issi-gift-finder__result-body';

      const badge = document.createElement('span');
      badge.className = 'issi-gift-finder__result-badge';
      badge.textContent = isFallback || item.reasons.length === 0
        ? this.config.labels.fallbackBadge
        : this.config.labels.recommendationReason.replace('__reason__', item.reasons.slice(0, 2).join(', '));

      const title = document.createElement('h5');
      title.className = 'issi-gift-finder__result-title';

      const titleLink = document.createElement('a');
      titleLink.href = product.url || '#';
      titleLink.textContent = product.title || product.handle || '';
      title.appendChild(titleLink);

      const price = document.createElement('p');
      price.className = 'price issi-gift-finder__result-price';
      price.textContent = product.priceFormatted || '';

      const action = document.createElement('a');
      action.className = 'button button--secondary issi-gift-finder__result-action';
      action.href = product.url || '#';
      action.textContent = this.config.labels.viewProduct;

      body.append(badge, title, price, action);
      article.append(media, body);

      return article;
    }

    focusFirstElement() {
      const focusable = this.getFocusableElements();
      const target = focusable[0] || this.dialog;
      target?.focus();
    }

    getFocusableElements() {
      if (!this.dialog) return [];

      return Array.from(this.dialog.querySelectorAll(focusableSelector)).filter((element) => {
        return !element.closest('[hidden]') && (element.offsetParent !== null || element === document.activeElement);
      });
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        this.close();
        return;
      }

      if (!this.isPopup || event.key !== 'Tab') return;

      const focusable = this.getFocusableElements();
      if (focusable.length === 0) {
        event.preventDefault();
        this.dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    }
  }

  const initGiftFinders = (container = document) => {
    container.querySelectorAll(selectors.root).forEach((root) => {
      if (root.dataset.issiGiftFinderInitialized === 'true') return;

      root.dataset.issiGiftFinderInitialized = 'true';
      new IssiGiftFinder(root);
    });
  };

  initGiftFinders();

  document.addEventListener('shopify:section:load', (event) => {
    initGiftFinders(event.target);
  });
})();
